import { Button, TextField, toast } from "@3sc/common-component";
import { Grid, Stack, styled, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import CustomPhoneInput from "../../components/CustomPhoneInput";
import { nameValidation, userEditInitialValues } from "../../constant/constant";
import {
  createPayloadUserEdit,
  createUserData,
} from "../../helper/commonHelper";
import {
  loaderStateChange,
  resetLoaderState,
} from "../../store/Slices/loading";
import { editUserAPI, getUserAPI } from "../../utils/ApiHandler";
import { STATUS_CODE } from "../../utils/const";
import { COM_MSG, ERROR_MSG } from "../../utils/messages";
import "./styles.scss";
import { validateField } from "../../helper/validationHelper";
import { useAuth } from "../../provider/AuthContext";

import React, { useCallback, useEffect, useState } from "react";
// ===============================
// Styled Components
// ===============================
const SCAIGrid = styled(Grid)(({ theme }) => ({
  margin: "0!important",
  padding: "1.5rem!important",
  width: "100%",
  backgroundColor: theme.palette.primary.background,
  borderRadius: "0.7rem",
}));

const SCAIGridItem = styled(Grid)(({ theme }) => ({
  padding: "1rem!important",
  width: "100%",
}));

// ===============================
// Field Config (Single source of truth)
// ===============================
const PROFILE_FIELDS = {
  first_name: {
    component: "TextField",
    name: "first_name",
    labelKey: "first_name",
    required: true,
    validation: nameValidation,
    grid: { size: { xs: 12, md: 2.4 } },
  },
  last_name: {
    component: "TextField",
    name: "last_name",
    labelKey: "last_name",
    required: true,
    validation: nameValidation,
    grid: { size: { xs: 12, md: 2.4 } },
  },
  email: {
    component: "TextField",
    name: "email",
    labelKey: "email_id",
    disabled: true,
    required: true,
    validation: "required|email",
    grid: { size: { xs: 12, md: 2.4 } },
  },
  mobile: {
    component: "Phone",
    name: "mobile",
    labelKey: "contact_number",
    required: true,
    validation: ["required", "phone", { max: "13" }],
    grid: { size: { xs: 12, md: 2.4 } },
  },
  empty: {
    component: "empty",
  },
  organization: {
    component: "TextField",
    name: "organization",
    labelKey: "client",
    disabled: true,
    required: true,
    grid: { size: { xs: 12, md: 2.4 } },
  },
  business_unit: {
    component: "TextField",
    name: "business_unit",
    labelKey: "business_unit",
    disabled: true,
    required: true,
    grid: { size: { xs: 12, md: 2.4 } },
  },
  team: {
    component: "TextField",
    name: "team",
    labelKey: "team",
    disabled: true,
    required: true,
    grid: { size: { xs: 12, md: 2.4 } },
  },
  roles: {
    component: "TextField",
    name: "roles",
    labelKey: "Roles",
    disabled: true,
    required: true,
    grid: { size: { xs: 12, md: 2.4 } },
  },
  textField: {
    component: "textField",
  },
};

// ===============================
// Component
// ===============================
function MyProfile() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { tenantId, user, isLoading: isAuthLoading } = useAuth();
  const userId = user?.user_id;

  const [formData, setFormData] = useState(userEditInitialValues);
  const [errors, setErrors] = useState({});

  // ===============================
  // Fetch User Data
  // ===============================
  const fetchUserData = useCallback(async () => {
    if (!tenantId || !userId) return;

    try {
      dispatch(loaderStateChange({ isLoading: true, isParent: true }));

      const { status, data } = await getUserAPI({
        orgId: tenantId,
        userId,
      });

      if (STATUS_CODE.OK !== status) throw new Error(ERROR_MSG.SWR);

      const newFormData = createUserData({ data: data?.data });
      setFormData(newFormData);
    } catch {
      toast.error(t(ERROR_MSG.SWR));
    } finally {
      dispatch(resetLoaderState());
    }
  }, [dispatch, t, tenantId, userId]);

  // AuthContext's own refresh() (re-run on every mount, including a hard
  // reload) resolves tenantId/userId asynchronously — reading them via
  // useAuth() instead of the old non-reactive appState snapshot means this
  // effect re-fires once they actually land, instead of firing once at
  // mount (while they're still empty) and never retrying.
  useEffect(() => {
    if (isAuthLoading) return;
    fetchUserData();
  }, [isAuthLoading, fetchUserData]);

  // ===============================
  // Handlers
  // ===============================
  const handleChange = useCallback((name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "mobile") return;
    const error = validateField(value, PROFILE_FIELDS[name]?.validation);;

    setErrors((prev) => {
      if (error === prev[name]) return prev;

      const updated = { ...prev };

      if (error) {
        updated[name] = error;
      } else {
        delete updated[name];
      }

      return updated;
    });
  }, []);


  const handleNativeChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      handleChange(name, value);
    },
    [handleChange]
  );


  // ===============================
  // Submit Logic
  // ===============================
  const handleSubmit = useCallback(async () => {
    try {
      dispatch(loaderStateChange({ isLoading: true, isParent: true }));

      const payload = createPayloadUserEdit(formData, userId);

      const { status, data } = await editUserAPI({
        payload,
        orgId: tenantId,
        userId,
      });

      if (STATUS_CODE.OK !== status) throw new Error(ERROR_MSG.SWR);

      toast.success(t(data?.RESPONSE_CODE));
    } catch (error) {
      const resErr = JSON.stringify(error?.response?.data);
      toast.error(t(resErr));
    } finally {
      dispatch(resetLoaderState());
    }
  }, [dispatch, formData, t, tenantId, userId]);

  // ===============================
  // Renderer
  // ===============================
  const renderField = (field) => {
    const gridProps = field.grid;
    const commonProps = {
      name: field.name,
      label: t(field.labelKey),
      value: formData[field.name] || "",
      disabled: field.disabled,
    };

    switch (field.component) {
      case "TextField":
        return (
          <SCAIGridItem item {...gridProps} key={field.name}>
            <TextField
              {...commonProps}
              required={field.required}
              onChange={handleNativeChange}
            />
            <span className="custom-error">
              {errors[field.name] && t(errors[field.name])}
            </span>
          </SCAIGridItem>
        );

      case "Phone":
        return (
          <SCAIGridItem item {...gridProps} key={field.name}>
            <CustomPhoneInput
              label={t(field.labelKey)}
              value={formData.mobile}
              onChange={(val) => handleChange("mobile", val)}
              setErrors={setErrors}
            />
          </SCAIGridItem>
        );

      default:
        return <SCAIGridItem item {...gridProps} />;
    }
  };

  // ===============================
  // UI
  // ===============================
  return (
    <>
      <Stack
        direction={"column"}
        sx={{
          padding: "1.5rem",
          margin: "1.5rem",
          border: (theme) =>
            `0.1rem solid ${theme.palette.primary.borderColor}`,
          borderRadius: "0.8rem",
          gap: "1rem",
          height: "-webkit-fill-available",
        }}
      >
        <Typography
          sx={{
            color: (theme) => theme.palette.primary.activeText,
            fontSize: "1.4rem",
          }}
        >
          {t("my_profile")}
        </Typography>

        <Stack direction={"column"} justifyContent={"space-between"}>
          <SCAIGrid container spacing={3}>
            {Object.values(PROFILE_FIELDS).map(renderField)}
          </SCAIGrid>

          <Stack direction={"row-reverse"} sx={{
            p: "1rem 0.5rem"
          }}>
            <Button
              label={t("update")}
              styles={{ variant: "filled" }}
              disabled={Object.keys(errors)?.length}
              onClick={handleSubmit}
            />
          </Stack>
        </Stack>
      </Stack>
    </>
  );
}

export default MyProfile;
